import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogTitle, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { useNotify, useRedirect } from 'react-admin';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { money } from '../../utils/money.ts';
import { errorText, type ShabbatSummary, type ShabbatVenue, type VendorMeal } from '../../services/shabbat/shabbatService.ts';
import { MealEditor } from './MealEditor.tsx';
import {
    NotHostingNote, STATUS_CHIP, ShabbatFrame, StatusChip, fmtDate, fmtDateTime, fmtInstant, hostingVenues, mealName, plural,
    shabbatService, venueLabel,
} from './shabbatShared.tsx';

const Tile: React.FC<{ label: string; value: string; sub: string }> = ({ label, value, sub }) => (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', px: 2.25, py: 2 }}>
        <Typography sx={{ fontSize: 12.5, color: '#6B7280' }}>{label}</Typography>
        <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#111827', mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
        <Typography sx={{ fontSize: 12.5, color: '#03676A', mt: 0.25 }}>{sub}</Typography>
    </Box>
);

/** "Closes Thu 15 Oct · 18:00 · Closes at 40 guests · Donations on", from the meal's own rules. */
const closeLine = (m: VendorMeal, timeZone: string): string => [
    m.closesAt ? `Closes ${fmtInstant(m.closesAt, timeZone)}` : null,
    m.closesAtGuests != null ? `Closes at ${m.closesAtGuests} guests` : null,
    !m.closesAt && m.cancelUntil ? `Cancellations until ${fmtInstant(m.cancelUntil, timeZone)}` : null,
    m.acceptDonations ? 'Donations on' : null,
].filter(Boolean).join(' · ');

/** onEdit null: the venue's switch is off, its meals are read-only. */
const MealCard: React.FC<{ meal: VendorMeal; venue: ShabbatVenue; showVenue: boolean; onEdit: (() => void) | null }> = ({ meal: m, venue, showVenue, onEdit }) => {
    const redirect = useRedirect();
    const chip = STATUS_CHIP[m.status] ?? STATUS_CHIP.OPEN;
    const pct = m.seats > 0 ? Math.min(100, Math.round((m.guests / m.seats) * 100)) : 0;
    return (
        <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', px: 2.5, py: 2.25, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {showVenue && (
                <Typography data-venue-name="1" sx={{ fontSize: 12.5, fontWeight: 700, color: '#03676A', textTransform: 'uppercase', letterSpacing: '.4px' }} noWrap>
                    {m.locationName ?? venue.name}{venue.cityName ? ` · ${venue.cityName}` : ''}{venue.hostsShabbat ? '' : ' (not hosting now)'}
                </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Typography sx={{ flex: 1, fontSize: 17, fontWeight: 700, color: '#111827' }}>{mealName(m.type)}</Typography>
                <StatusChip {...chip} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14, color: '#374151' }}>
                <AccessTimeIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                <span>{fmtDateTime(m.date, m.startTime)}</span>
            </Box>
            <Typography sx={{
                fontSize: 13, color: '#6B7280', lineHeight: 1.35, overflow: 'hidden',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: '2.7em',
            }}>
                {m.menu || 'No menu written yet'}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#374151' }}>
                    <span style={{ fontWeight: 700 }}>{m.guests} / {m.seats} guests</span>
                    <span>{plural(m.registrations, 'registration', 'registrations')}{m.pendingGuests > 0 ? ` · ${m.pendingGuests} paying now` : ''}</span>
                </Box>
                <Box sx={{ height: 8, borderRadius: '4px', bgcolor: '#E5E7EB', mt: 0.75, overflow: 'hidden' }}>
                    <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: m.status === 'FULL' ? '#DC2626' : '#24BBAA', borderRadius: '4px' }} />
                </Box>
            </Box>
            <Typography sx={{ fontSize: 12.5, color: '#6B7280' }}>{closeLine(m, venue.timeZone)}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 0.75 }}>
                <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 700, color: '#111827' }}>
                    {money(m.adultPrice, m.currency)} adult · {money(m.childPrice, m.currency)} child
                </Typography>
                <Button
                    size="small" variant="outlined" onClick={() => redirect(`/shabbat/registrations?venue=${m.locationId}&meal=${m.id}`)}
                    sx={{ borderRadius: '17px', borderWidth: 1.5, borderColor: '#24BBAA', color: '#03676A', fontWeight: 700, px: 1.75 }}
                >
                    Registrations
                </Button>
                {onEdit && <Button
                    size="small" onClick={onEdit} startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                    sx={{ borderRadius: '17px', bgcolor: '#F3F4F6', color: '#111827', fontWeight: 700, px: 1.75, '&:hover': { bgcolor: '#E5E7EB' } }}
                >
                    Edit
                </Button>}
            </Box>
        </Box>
    );
};

type Editor = { meal: VendorMeal | null; venue: ShabbatVenue | null } | null;

/**
 * One venue's Shabbat, or every venue's together (venue null: an admin's
 * default since 23 Oct 2026) - the tiles then add up all venues and each card
 * names its venue.
 */
const VenueMeals: React.FC<{ venue: ShabbatVenue | null; venues: ShabbatVenue[]; editor: Editor; setEditor: (e: Editor) => void }> = ({ venue, venues, editor, setEditor }) => {
    const notify = useNotify();
    const [summary, setSummary] = useState<ShabbatSummary | null>(null);
    const [meals, setMeals] = useState<VendorMeal[] | null>(null);
    const [failed, setFailed] = useState<string | null>(null);
    const id = venue?.id ?? null;
    const all = venue === null;

    const load = useCallback(async () => {
        setFailed(null);
        try {
            const [s, list] = await Promise.all([shabbatService.summary(id), shabbatService.meals(id)]);
            setSummary(s);
            setMeals(list);
        } catch (e) {
            setFailed(errorText(e, 'Could not load the meals. Reload the page to try again.'));
        }
    }, [id]);

    useEffect(() => { setSummary(null); setMeals(null); load(); }, [load]);

    if (failed) return <Alert severity="error" sx={{ maxWidth: 760 }}>{failed}</Alert>;
    if (!summary || !meals) return <CircularProgress size={24} />;

    const venueOf = (m: VendorMeal) => venues.find(v => v.id === m.locationId) ?? venue ?? venues[0];
    const thisShabbat = meals.filter(m => m.date >= summary.friday && m.date <= summary.saturday);
    const later = meals.filter(m => m.date > summary.saturday);
    const cur = summary.currency || venue?.currency || 'EUR';
    const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(380px, 100%), 1fr))', gap: 2, mt: 1.75 };
    const card = (m: VendorMeal) => (
        <MealCard key={m.id} meal={m} venue={venueOf(m)} showVenue={all}
            onEdit={venueOf(m).hostsShabbat ? () => setEditor({ meal: m, venue: venueOf(m) }) : null} />
    );
    // New meals only at a venue hosting now (the server refuses the others too).
    const canAdd = venue ? venue.hostsShabbat : hostingVenues(venues).length > 0;

    return (
        <>
            <NotHostingNote venue={venue} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 1.75 }}>
                <Tile label={all ? 'Guests this Shabbat · all venues' : 'Guests this Shabbat'} value={String(summary.guests)} sub={plural(summary.registrations, 'registration', 'registrations')} />
                <Tile label="Seats left" value={String(summary.seatsLeft)} sub={plural(summary.mealsFull, 'meal full', 'meals full')} />
                <Tile label="Collected" value={money(summary.collected, cur)} sub="paid in the app" />
                <Tile label="Donations" value={money(summary.donations, cur)} sub={`${plural(summary.donationsCount, 'guest', 'guests')} added a donation`} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 3.5 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#111827' }}>This Shabbat</Typography>
                <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{fmtDate(summary.friday)} – {fmtDate(summary.saturday)}</Typography>
            </Box>

            {thisShabbat.length === 0 ? (
                <Box sx={{ bgcolor: '#fff', border: '1px dashed #D1D5DB', borderRadius: '12px', py: 6, px: 3, textAlign: 'center', mt: 1.75 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>{all ? 'No venue has meals this Shabbat' : 'No meals this Shabbat'}</Typography>
                    {canAdd && <>
                        <Typography sx={{ fontSize: 13.5, color: '#6B7280', mt: 0.75 }}>Add the meals you are hosting and guests can register in the Vello app.</Typography>
                        <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setEditor({ meal: null, venue })} sx={{ mt: 2, borderRadius: '20px', color: '#fff' }}>
                            New meal
                        </Button>
                    </>}
                </Box>
            ) : (
                <Box sx={grid}>{thisShabbat.map(card)}</Box>
            )}

            {later.length > 0 && (
                <>
                    <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#111827', mt: 3.5 }}>Later</Typography>
                    <Box sx={grid}>{later.map(card)}</Box>
                </>
            )}

            {editor && !editor.venue && (
                <Dialog open onClose={() => setEditor(null)} PaperProps={{ sx: { borderRadius: '16px', minWidth: 320 } }}>
                    <DialogTitle sx={{ fontWeight: 700 }}>New meal at which venue?</DialogTitle>
                    <List sx={{ pt: 0, pb: 1.5 }}>
                        {hostingVenues(venues).map(v => (
                            <ListItemButton key={v.id} onClick={() => setEditor({ meal: null, venue: v })}>
                                <ListItemText primary={v.name} secondary={[v.cityName, v.shabbatOnly ? 'Shabbat only' : null].filter(Boolean).join(' · ')} />
                            </ListItemButton>
                        ))}
                    </List>
                </Dialog>
            )}
            {editor && editor.venue && (
                <MealEditor
                    venue={editor.venue}
                    friday={summary.friday}
                    meal={editor.meal}
                    onClose={() => setEditor(null)}
                    onSaved={(message) => { setEditor(null); notify(message, { type: 'success' }); load(); }}
                />
            )}
        </>
    );
};

/** Shabbat meals: this week's tiles, one card per meal, and the editor. Admins start on all venues. */
const ShabbatMealsPage = () => {
    const [editor, setEditor] = useState<Editor>(null);
    return (
        <ShabbatFrame
            title="Shabbat meals"
            subtitle="Plan this week's meals and watch registrations come in"
            allowAll
            actions={(venue, venues) => (
                <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setEditor({ meal: null, venue })}
                    disabled={venue ? !venue.hostsShabbat : hostingVenues(venues).length === 0}
                    title={venue && !venue.hostsShabbat ? `${venueLabel(venue)}: switch "Hosts Shabbat meals" on to add meals` : undefined}
                    sx={{ borderRadius: '20px', color: '#fff', height: 40, px: 2.25 }}>
                    New meal
                </Button>
            )}
        >
            {(venue, venues) => <VenueMeals key={venue?.id ?? 'all'} venue={venue} venues={venues} editor={editor} setEditor={setEditor} />}
        </ShabbatFrame>
    );
};

export default ShabbatMealsPage;
